import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, PageHeader, Skeleton } from "@/components/ui";

interface NotificationRule {
  notification_type: string;
  recipient_kind: string;
  enabled: boolean;
}

interface Option {
  value: string;
  label: string;
  always?: boolean;
  internal?: boolean;
}

interface RulesResponse {
  rules: NotificationRule[];
  types: Option[];
  recipient_kinds: Option[];
}

/**
 * Who receives which notification.
 *
 * A matrix, not a list of toggles per screen: the question an operator
 * actually has is "does the ops team see tracking exceptions", which is a
 * row-and-column lookup, not something worth four separate pages to answer.
 *
 * Both axes come from the API. The labels used to be duplicated here, which
 * meant every new notification type needed a Go constant and a TypeScript map
 * to be changed together, and showed up as a blank row when they weren't.
 */
export function PlatformNotifications() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["notification-rules"],
    queryFn: () => api.get<RulesResponse>("/v1/admin/notifications/rules"),
  });

  const toggle = useMutation({
    mutationFn: (rule: NotificationRule) =>
      api.put(`/v1/admin/notifications/rules/${rule.notification_type}/${rule.recipient_kind}`, {
        enabled: !rule.enabled,
      }),
    // Optimistic: a checkbox that waits for a round trip before it moves
    // feels broken, and worst case a refetch corrects it a moment later.
    onMutate: async (rule) => {
      await queryClient.cancelQueries({ queryKey: ["notification-rules"] });
      const previous = queryClient.getQueryData<RulesResponse>(["notification-rules"]);
      queryClient.setQueryData<RulesResponse>(["notification-rules"], (current) =>
        current
          ? {
              ...current,
              rules: current.rules.map((r) =>
                r.notification_type === rule.notification_type && r.recipient_kind === rule.recipient_kind
                  ? { ...r, enabled: !r.enabled }
                  : r,
              ),
            }
          : current,
      );
      return { previous };
    },
    onError: (_err, _rule, context) => {
      if (context?.previous) queryClient.setQueryData(["notification-rules"], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["notification-rules"] }),
  });

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Notifications" subtitle="Who receives which email, per notification type." />
        <Card>
          <Skeleton className="h-64 w-full" />
        </Card>
      </>
    );
  }

  const byCell = new Map(data.rules.map((r) => [`${r.notification_type}|${r.recipient_kind}`, r]));

  return (
    <>
      <PageHeader title="Notifications" subtitle="Who receives which email, per notification type." />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-ink-400">
                <th className="py-2 pr-4 text-left">Notification</th>
                {data.recipient_kinds.map((kind) => (
                  <th key={kind.value} className="px-2 py-2 text-center font-medium">
                    {kind.label}
                    {kind.always && <span className="mt-0.5 block text-[10px] normal-case text-brand">Always</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200">
              {data.types.map((type) => (
                <tr key={type.value}>
                  <td className="py-3 pr-4 font-medium">{type.label}</td>
                  {data.recipient_kinds.map((kind) => {
                    const rule =
                      byCell.get(`${type.value}|${kind.value}`) ??
                      ({ notification_type: type.value, recipient_kind: kind.value, enabled: false } as NotificationRule);
                    return (
                      <td key={kind.value} className="px-2 py-3 text-center">
                        {/* An always-notified recipient is shown ticked and
                            disabled rather than as a live checkbox: the send
                            path ignores the rule, so a box that moved here
                            would be telling an operator something untrue. */}
                        <input
                          type="checkbox"
                          checked={rule.enabled}
                          disabled={kind.always}
                          onChange={() => !kind.always && toggle.mutate(rule)}
                          className="h-4 w-4 rounded border-ink-300 text-brand disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label={`${type.label} to ${kind.label}`}
                          title={kind.always ? `${kind.label} receives every notification` : undefined}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-ink-400">
          The WeShip super admin receives every notification on every account and cannot be switched off — that is
          what makes head office the first to know. It resolves to super admin and platform admin logins, plus the
          PLATFORM_NOTIFICATION_EMAIL mailbox. The user involved is the one person an event is about, such as someone
          newly invited. Booking recipient is the delivery contact on the shipment itself, not the account holder.
        </p>
      </Card>
    </>
  );
}
